import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {lerpGreenToRedViaOrange} from "../../helpers/scaling";
import {scaleNumber} from "../../lq_js_common/helpers/scaling";
import {debugLog} from '../../helpers/debug_log';
import {registerGraph, unregisterGraph} from "../../helpers/graph_registry";
import {
    attachTimeCrosshairSync,
    detachTimeCrosshairSync,
    invalidateTimeCrosshairAxisCache,
    refreshTimeCrosshairAccessibility,
} from "../../helpers/time_crosshair_sync";

export class ShaperCircuitTimeline extends BaseDashlet {
    constructor(slot) {
        super(slot);
        this.requestInFlight = false;
        this.MAX_ROWS = 50000; // match server-side safety cap
        this.btnId = this.id + '_fetchTimelineBtn';
        this.btnWrapId = this.id + '_btnWrap';
        this.spinnerId = this.id + '_spinner';
        this.spinWrapId = this.id + '_spinWrap';
        this.countId = this.id + '_rowCount';
        this.messageId = this.id + '_message';
        this.scopeId = this.id + '_scope';
        this.rowCount = 0;
        this.timelineStreamActive = false;

        // In-memory storage for streamed rows
        this.timeline = {
            byAsn: new Map(), // name -> { totalBytes, flows: [{start, end, bytes, rtt, rxmit}] }
        };
        this.chart = null;
        this.syncGraph = null;
    }

    setup() {
        super.setup();
        // Register for global time period changes so we can reset the widget
        const handler = { onTimeChange: () => { this.#resetForNewTime(); } };
        this.registerTimeGraphHandler(handler);
    }

    title() { return "Circuit Timeline"; }

    channelRequests() { return []; }

    buildContainer() {
        const base = super.buildContainer();

        const btnWrap = document.createElement('div');
        btnWrap.classList.add('d-flex', 'justify-content-center', 'my-2');
        btnWrap.id = this.btnWrapId;

        const startButton = document.createElement('button');
        startButton.type = 'button';
        startButton.id = this.btnId;
        startButton.classList.add('btn', 'btn-secondary', 'btn-sm');
        startButton.innerHTML = "<i class='fa fa-download'></i> Fetch Timeline";
        startButton.onclick = () => {
            if (this.requestInFlight) return;
            // CBOR Request for Inbound::CircuitTimeline { circuit_hash }
            const msg = ["CircuitTimeline", {
                node_id: window.nodeId,
                circuit_hash: window.circuitId,
                all_shapers: localStorage.getItem("lqos_all_shapers_default") !== "false",
            }];
            this.requestInFlight = true;
            // Hide the button immediately to prevent duplicate requests
            const wrap = document.getElementById(this.btnWrapId);
            if (wrap) {
                debugLog("Hiding button wrap");
                wrap.classList.add('d-none');
            }
            if (window.ws && window.ws.send) {
                window.ws.send(msg);
            }
        };

        btnWrap.appendChild(startButton);

        const scope = document.createElement('span');
        scope.id = this.scopeId;
        scope.classList.add('ms-2', 'small', 'text-muted', 'align-self-center');
        btnWrap.appendChild(scope);
        base.appendChild(btnWrap);
        this.#updateScopeLabel();

        // Spinner (hidden until we get Starting)
        const spinWrap = document.createElement('div');
        spinWrap.classList.add('d-flex', 'justify-content-center', 'my-2', 'd-none');
        spinWrap.id = this.spinWrapId;
        const spinner = document.createElement('div');
        spinner.id = this.spinnerId;
        spinner.style.display = 'none';
        spinner.innerHTML = "<div class='spinner-border spinner-border-sm text-secondary' role='status'><span class='visually-hidden'>Loading...</span></div>";
        spinWrap.appendChild(spinner);
        // Row counter text
        const counter = document.createElement('span');
        counter.id = this.countId;
        counter.classList.add('ms-2', 'small', 'text-muted');
        counter.textContent = '';
        spinWrap.appendChild(counter);
        base.appendChild(spinWrap);

        const message = document.createElement('div');
        message.id = this.messageId;
        message.classList.add('d-none', 'mb-2');
        message.setAttribute('role', 'alert');
        message.setAttribute('aria-live', 'polite');
        base.appendChild(message);

        // Graph container
        const graph = this.graphDiv();
        // Give the graph more vertical space by default
        graph.style.height = '520px';
        this.graphEl = graph;
        base.appendChild(graph);

        return base;
    }

    onMessage(msg) {
        if (!msg.Data || msg.Data.target !== "circuit_timeline") return;
        const data = msg.Data.blob || {};
        const spinner = document.getElementById(this.spinnerId);
        const spinWrap = document.getElementById(this.spinWrapId);
        const btnWrap = document.getElementById(this.btnWrapId);
        const message = document.getElementById(this.messageId);
        const state = data.state;

        // Support both unit variants as strings ("Starting") and externally-tagged objects ({Starting: {}})
        let variant = null;
        let payload = null;
        if (typeof state === 'string') {
            variant = state;
        } else if (state && typeof state === 'object') {
            if (Object.prototype.hasOwnProperty.call(state, 'Starting')) {
                variant = 'Starting';
                payload = state.Starting;
            } else if (Object.prototype.hasOwnProperty.call(state, 'Streaming')) {
                variant = 'Streaming';
                payload = state.Streaming;
            } else if (Object.prototype.hasOwnProperty.call(state, 'Error')) {
                variant = 'Error';
                payload = state.Error;
            }
        }

        if (variant === 'Starting') {
            this.timelineStreamActive = true;
            // Hide the button in case it wasn't hidden on click
            if (btnWrap) btnWrap.classList.add('d-none');
            if (spinWrap) spinWrap.classList.remove('d-none');
            if (spinner) spinner.style.display = '';
            this.#clearMessage(message);
            this.requestInFlight = true;
            // Reset timeline buffer
            this.timeline = { byAsn: new Map() };
            this.rowCount = 0;
            const counter = document.getElementById(this.countId);
            if (counter) counter.textContent = '0 rows';
            // Clear chart content if present
            const div = document.getElementById(this.graphDivId());
            this.#disposeChart();
            if (div) div.innerHTML = '';
        } else if (variant === 'Streaming') {
            if (!this.timelineStreamActive || !this.requestInFlight) {
                return;
            }
            payload = payload || {};
            const rows = payload.rows || [];
            this.#clearMessage(message);
            this.#ingestRows(rows);
            // Update row count indicator
            this.rowCount += rows.length;
            const counter = document.getElementById(this.countId);
            if (counter) counter.textContent = `${this.rowCount.toLocaleString()} rows`;
            if (spinner && payload.final_batch === true) {
                spinner.style.display = 'none';
                const truncated = this.rowCount >= this.MAX_ROWS;
                // If truncated, keep the spinWrap visible with a notice; otherwise hide it
                if (spinWrap) {
                    if (truncated) {
                        const notice = ` (${this.MAX_ROWS.toLocaleString()} cap reached)`;
                        const counter = document.getElementById(this.countId);
                        if (counter && !counter.textContent.includes('cap')) {
                            counter.textContent = `${this.rowCount.toLocaleString()} rows${notice}`;
                        }
                        spinWrap.classList.remove('d-none');
                    } else {
                        spinWrap.classList.add('d-none');
                    }
                }
                // Allow user to fetch again if needed
                this.requestInFlight = false;
                this.timelineStreamActive = false;
                if (btnWrap) btnWrap.classList.remove('d-none');

                this.#renderTimeline();
            }
        } else if (variant === 'Error') {
            if (spinner) spinner.style.display = 'none';
            if (spinWrap) spinWrap.classList.add('d-none');
            this.requestInFlight = false;
            this.timelineStreamActive = false;
            if (btnWrap) btnWrap.classList.remove('d-none');
            this.#showMessage(message, this.#friendlyErrorMessage(payload?.message));
        }
    }

    #clearMessage(messageEl) {
        if (!messageEl) return;
        messageEl.className = 'd-none mb-2';
        messageEl.textContent = '';
    }

    #showMessage(messageEl, text) {
        if (!messageEl) return;
        messageEl.className = 'alert alert-warning small mb-2';
        messageEl.textContent = text;
    }

    #friendlyErrorMessage(rawMessage) {
        const message = String(rawMessage || '').trim();
        if (message.includes('older than 24 hours')) {
            return 'Timeline data is only available for the most recent 24 hours. Choose a newer time range and fetch again.';
        }
        if (message.includes('start must be before end')) {
            return 'Choose a valid time range and fetch the timeline again.';
        }
        return message || 'Timeline data could not be loaded.';
    }

    #ingestRows(rows) {
        // Accumulate by ASN
        for (let i=0; i<rows.length; i++) {
            const r = rows[i];
            const name = r.asn_name || 'Unknown';
            const startMs = Number(r.start_epoch) * 1000;
            const endMs = Number(r.end_epoch) * 1000;
            const bytes = Number(r.bytes_down || 0) + Number(r.bytes_up || 0);
            // choose a quality metric for color (prefer RTT down, else up)
            const rtt = Number(r.rtt_down_ms || r.rtt_up_ms || 0);
            const rxmit = Math.max(Number(r.retransmit_percent_down || 0), Number(r.retransmit_percent_up || 0));
            const lp = Number(r.local_port || 0);
            const rp = Number(r.remote_port || 0);

            let bucket = this.timeline.byAsn.get(name);
            if (!bucket) {
                bucket = { totalBytes: 0, flows: [] };
                this.timeline.byAsn.set(name, bucket);
            }
            bucket.totalBytes += bytes;
            // protocol present on server rows; default 0 if missing
            const proto = Number(r.protocol || 0);
            bucket.flows.push({ start: startMs, end: endMs, bytes, rtt, rxmit, proto, lp, rp });
        }
    }

    #renderTimeline() {
        // Build category order by totalBytes desc
        const entries = Array.from(this.timeline.byAsn.entries());
        entries.sort((a,b) => b[1].totalBytes - a[1].totalBytes);
        // Limit to top N for performance, group others
        const TOP_N = 25;
        const categories = [];
        const items = [];

        const top = entries.slice(0, TOP_N);
        const rest = entries.slice(TOP_N);

        // Add top categories and their items
        for (let i=0; i<top.length; i++) {
            const [name, bucket] = top[i];
            const y = categories.length;
            categories.push(name);
            bucket.flows.forEach(f => {
                items.push({ name, value: [f.start, y, f.end, f.bytes, f.rtt, f.rxmit, f.proto, f.lp, f.rp] });
            });
        }
        // Optionally group others as 'Other'
        if (rest.length > 0) {
            let otherBytes = 0;
            let otherFlows = [];
            rest.forEach(([_, bucket]) => {
                otherBytes += bucket.totalBytes;
                otherFlows = otherFlows.concat(bucket.flows);
            });
            const y = categories.length;
            categories.push('Other');
            otherFlows.forEach(f => {
                items.push({ name: 'Other', value: [f.start, y, f.end, f.bytes, f.rtt, f.rxmit, f.proto, f.lp, f.rp] });
            });
        }

        // Create chart if needed
        const div = document.getElementById(this.graphDivId());
        if (!div) return;
        if (!this.chart) {
            // eslint-disable-next-line no-undef
            this.chart = echarts.init(div);
            this.syncGraph = { dom: div, chart: this.chart };
            registerGraph(this.syncGraph);
            attachTimeCrosshairSync(this.syncGraph);
        }

        // Dynamically scale height to number of categories
        const perRow = 22; // px per ASN row
        const minHeight = 360;
        const desired = Math.max(minHeight, categories.length * perRow + 80);
        if (this.graphEl) {
            this.graphEl.style.height = desired + 'px';
        }
        if (this.chart) {
            this.chart.resize();
        }

        const option = this.#buildOption(categories, items);
        this.chart.setOption(option, true);
        invalidateTimeCrosshairAxisCache(this.syncGraph);
        refreshTimeCrosshairAccessibility(this.syncGraph);
    }

    #buildOption(categories, items) {
        const self = this;
        return {
            animation: false,
            tooltip: {
                trigger: 'item',
                axisPointer: {
                    type: 'cross',
                    label: {
                        // Force readable styling regardless of global CSS
                        backgroundColor: 'rgba(50,50,50,0.85)',
                        color: '#fff',
                        formatter: (p) => {
                            // p.value is the axis value for the pointer on xAxis (timestamp ms)
                            if (p && p.axisDimension === 'x') {
                                const ts = Number(p.value);
                                if (!isNaN(ts)) return new Date(ts).toLocaleString();
                            }
                            return '';
                        }
                    }
                },
                // Ensure tooltip remains readable (fixes GH issue #11)
                backgroundColor: 'rgba(0,0,0,0.85)',
                borderColor: 'rgba(0,0,0,0.2)',
                borderWidth: 1,
                textStyle: { color: '#fff' },
                // Guard against external CSS overrides
                extraCssText: 'backdrop-filter: blur(0.5px); color:#fff; background: rgba(0,0,0,0.85); border:1px solid rgba(0,0,0,0.2);',
                formatter: (p) => {
                    const v = p.value;
                    const start = new Date(v[0]);
                    const end = new Date(v[2]);
                    const bytes = Number(v[3] || 0);
                    const rtt = Number(v[4] || 0);
                    const rx = Number(v[5] || 0);
                    const proto = Number(v[6] || 0);
                    const lp = Number(v[7] || 0);
                    const rp = Number(v[8] || 0);
                    let protoStr = `proto ${proto}`;
                    if (proto === 6) protoStr = 'TCP';
                    else if (proto === 17) protoStr = 'UDP';
                    else if (proto === 1) protoStr = 'ICMP';

                    let html = `${p.name}<br/>${start.toLocaleString()} - ${end.toLocaleString()}<br/>Bytes: ${scaleNumber(bytes, 1)}<br/>Protocol: ${protoStr}`;
                    if (lp || rp) {
                        html += ` | Ports: ${lp} → ${rp}`;
                    }
                    if (proto === 6) {
                        if (rtt > 0) html += `<br/>RTT: ${rtt.toFixed(1)} ms`;
                        if (rx > 0) html += `<br/>Rxmit: ${rx.toFixed(2)}%`;
                    }
                    return html;
                }
            },
            grid: { left: 140, right: 20, top: 20, bottom: 40 },
            xAxis: { type: 'time' },
            yAxis: { type: 'category', data: categories, inverse: true },
            dataZoom: [ { type: 'inside', xAxisIndex: 0 }, { type: 'slider', xAxisIndex: 0 } ],
            series: [
                {
                    type: 'custom',
                    renderItem: function(params, api) {
                        const yIndex = api.value(1);
                        const startCoord = api.coord([api.value(0), yIndex]);
                        const endCoord = api.coord([api.value(2), yIndex]);
                        const barHeight = api.size([0, 1])[1] * 0.7;
                        const width = Math.max(1, endCoord[0] - startCoord[0]);
                        const y = startCoord[1] - barHeight / 2;
                        const rtt = api.value(4) || 0;
                        const rx = api.value(5) || 0;
                        const proto = api.value(6) || 0;
                        // Protocol 6 is TCP; use neutral styling for non-TCP
                        let fillColor, strokeColor;
                        if (proto === 6) {
                            // Fill by retransmits (cap 10%), border by RTT (cap 200ms); neutral if value missing/zero
                            const neutralFill = 'rgba(128,128,128,0.4)';
                            const neutralStroke = 'rgba(160,160,160,0.9)';
                            fillColor = (rx > 0)
                                ? lerpGreenToRedViaOrange(Math.max(0, 10 - Math.min(10, rx)), 10)
                                : neutralFill;
                            strokeColor = (rtt > 0)
                                ? lerpGreenToRedViaOrange(Math.max(0, 200 - Math.min(200, rtt)), 200)
                                : neutralStroke;
                        } else {
                            fillColor = 'rgba(128,128,128,0.4)';
                            strokeColor = 'rgba(160,160,160,0.9)';
                        }
                        return {
                            type: 'rect',
                            shape: { x: startCoord[0], y, width, height: barHeight },
                            style: { fill: fillColor, stroke: strokeColor, lineWidth: 1.5, opacity: 0.95 },
                            emphasis: { style: { lineWidth: 2 } }
                        };
                    },
                    encode: { x: [0,2], y: 1 },
                    data: items
                }
            ]
        };
    }

    #resetForNewTime() {
        // Stop any in-flight UI state
        this.requestInFlight = false;
        this.timelineStreamActive = false;
        this.timeline = { byAsn: new Map() };
        this.rowCount = 0;
        const btnWrap = document.getElementById(this.btnWrapId);
        const spinWrap = document.getElementById(this.spinWrapId);
        const spinner = document.getElementById(this.spinnerId);
        const counter = document.getElementById(this.countId);
        const message = document.getElementById(this.messageId);
        if (btnWrap) btnWrap.classList.remove('d-none');
        if (spinWrap) spinWrap.classList.add('d-none');
        if (spinner) spinner.style.display = 'none';
        if (counter) counter.textContent = '';
        this.#clearMessage(message);
        // Clear the chart display
        const div = document.getElementById(this.graphDivId());
        this.#disposeChart();
        if (div) div.innerHTML = '';
        this.#updateScopeLabel();
    }

    #disposeChart() {
        if (this.syncGraph) {
            detachTimeCrosshairSync(this.syncGraph);
            unregisterGraph(this.syncGraph);
            this.syncGraph = null;
        }
        if (this.chart) {
            try { this.chart.dispose(); } catch (_) {}
            this.chart = null;
        }
    }

    dispose() {
        this.#disposeChart();
        super.dispose();
    }

    onScopeChange() {
        this.#resetForNewTime();
    }

    #updateScopeLabel() {
        const scope = document.getElementById(this.scopeId);
        if (!scope) return;
        const allShapers = localStorage.getItem("lqos_all_shapers_default") !== "false";
        scope.textContent = allShapers ? 'All shapers' : 'Selected shaper';
    }
}
